// The signal stream: `GET /v1/events`, and the one thing it is allowed to tell us.
//
// ---------------------------------------------------------------------------
// WHY NOT `EventSource`
// ---------------------------------------------------------------------------
//
// Because `EventSource` cannot set a request header, and the only way to authenticate it
// is `?token=…`. `packages/delivery/src/internals.ts` names that exact temptation and
// ships `tokenFromQueryString` as a MUTANT: a credential in a query string is a credential
// in an access log, in a `Referer`, and in browser history. The delivery layer reads the
// `Authorization` header and nothing else, so a browser client either streams over `fetch`
// or puts the token somewhere it must not go.
//
// So: `fetch` with a header, and the response body read as a stream. It costs the parser
// below — thirty lines the platform would otherwise have given us — and it buys the
// property that this client has no second place a token can live.
//
// ---------------------------------------------------------------------------
// WHAT ARRIVES, AND WHAT IS DONE WITH IT
// ---------------------------------------------------------------------------
//
// A frame carries a plan id and a timestamp and no content, by design and by test on the
// server side. This parser reads the plan id. It does not read `at`, and it will not read
// a `kind` or a `preview` or a `body` if one ever appears, because `decodeSignalPlanId`
// names one field and `onSignal` takes a string. A signal is a fact about which plan to
// re-read; it is never a description of what changed.
//
// ---------------------------------------------------------------------------
// RECONNECTION, AND WHY IT ENDS IN A FETCH
// ---------------------------------------------------------------------------
//
// This stream has no replay: the delivery layer emits no `id:` line and handles no
// `Last-Event-ID`, because "a missed signal costs a refetch the client does anyway". What
// makes that safe is the server's ordering — it snapshots a connection's plans BEFORE the
// response exists, so anything that changed before the response arrived is inside what our
// own fetch returns, and anything after it produces a signal.
//
// The client's half of that bargain is: once the response resolves, refetch. That is
// `onConnected`, and it fires on every connection including reconnections, which is what
// makes a dropped socket cost a re-read and not a stale screen.
//
// A dropped stream is NOT an error the person is told about. Losing the connection means
// the screen stops updating on its own; it does not mean anything is broken, and a family
// reading a conversation about a parent's death should not be shown a red bar because a
// proxy timed out. `onDisconnected` exists so the caller CAN know, and `usePlan`'s is a
// deliberate no-op — see the comment there. Nothing in this interface renders transport
// state.

import { decodeSignalPlanId } from './contract.js';

export type SignalStreamOptions = {
  baseUrl: string;
  /** Read at connect time. A reconnection picks up the current token. */
  token: () => string | null;
  /** A plan changed. Refetch it; the signal says nothing about how. */
  onSignal: (planId: string) => void;
  /** The stream is live. Fires on first connect and on every reconnect. */
  onConnected: () => void;
  /** The stream is not live. Not an error; the screen simply stops updating itself. */
  onDisconnected: () => void;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
  /** Backoff between attempts, in milliseconds. Exponential up to the last entry. */
  backoffMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
};

export const DEFAULT_BACKOFF_MS: readonly number[] = [
  1_000, 2_000, 5_000, 15_000, 30_000,
];

export type SignalStream = { close: () => void };

export function openSignalStream(options: SignalStreamOptions): SignalStream {
  const controller = new AbortController();
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const backoff = options.backoffMs ?? DEFAULT_BACKOFF_MS;
  const sleep =
    options.sleep ??
    ((ms: number) =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
      }));

  let attempt = 0;

  const run = async (): Promise<void> => {
    while (!controller.signal.aborted) {
      try {
        const token = options.token();
        if (token === null) {
          throw new Error('not signed in');
        }
        const response = await fetchImpl(`${options.baseUrl}/v1/events`, {
          method: 'GET',
          headers: { authorization: `Bearer ${token}`, accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!response.ok || response.body === null) {
          throw new Error(`the stream refused with ${response.status}`);
        }
        attempt = 0;
        options.onConnected();
        await consume(response.body, options.onSignal, controller.signal);
      } catch {
        // Every failure is the same failure: we are not connected. Nothing here is
        // reported to the person reading, and nothing here stops the loop.
      }
      if (controller.signal.aborted) {
        break;
      }
      options.onDisconnected();
      const wait = backoff[Math.min(attempt, backoff.length - 1)] ?? 30_000;
      attempt += 1;
      await sleep(wait);
    }
  };

  void run();

  return {
    close(): void {
      controller.abort();
    },
  };
}

/**
 * The SSE parser.
 *
 * Frames are separated by a blank line. A line beginning with `:` is a comment — the
 * heartbeat is `: heartbeat`, and it exists so proxies see traffic on an idle connection.
 * Only a frame whose event is `plan.changed` produces anything.
 */
async function consume(
  body: ReadableStream<Uint8Array>,
  onSignal: (planId: string) => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // CANCEL THE READER ON ABORT, as well as passing the signal to `fetch`.
  //
  // Not belt and braces. `reader.read()` on an idle stream is a promise that settles when
  // the server next writes — which, between heartbeats, is up to thirty seconds away and
  // may be never. Relying on `fetch`'s own signal handling to unblock it leaves this loop
  // holding a socket for a component that unmounted, and it is the reader's cancellation,
  // not the request's, that reaches the server: cancelling propagates to the response
  // body's source, which is what fires the delivery layer's `onCancel` and closes the
  // connection its end. Without this a person who moves between plans a few times leaves a
  // trail of live connections behind them.
  const cancel = (): void => {
    reader.cancel().catch(() => undefined);
  };
  signal.addEventListener('abort', cancel, { once: true });

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done || signal.aborted) {
        return;
      }
      buffer += decoder.decode(value, { stream: true });
      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        handleFrame(frame, onSignal);
        split = buffer.indexOf('\n\n');
      }
    }
  } finally {
    signal.removeEventListener('abort', cancel);
    reader.cancel().catch(() => undefined);
  }
}

function handleFrame(frame: string, onSignal: (planId: string) => void): void {
  let event = '';
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith(':') || line === '') {
      continue;
    }
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      data += line.slice('data:'.length).trim();
    }
    // Every other field — `id:`, `retry:`, anything invented later — is dropped. `id:` in
    // particular: the delivery layer emits none, and a client that stored one would be
    // holding a resume token for a stream that has no replay.
  }
  if (event !== 'plan.changed' || data === '') {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return;
  }
  let planId: string;
  try {
    planId = decodeSignalPlanId(parsed);
  } catch {
    return;
  }
  onSignal(planId);
}
